import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

import { PageShell } from "@/platform/layout/page-shell";
import { Button } from "@/platform/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/platform/ui/card";
import { Input } from "@/platform/ui/input";
import { Label } from "@/platform/ui/label";
import {
  passwordResetRequestSchema,
  passwordResetSchema,
  type PasswordResetRequestValues,
  type PasswordResetValues,
} from "@/platform/auth/schemas";
import { requestPasswordReset, updatePassword } from "@/platform/auth/api";

export function PasswordResetRequestPage() {
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const form = useForm<PasswordResetRequestValues>({
    resolver: zodResolver(passwordResetRequestSchema),
    defaultValues: { email: "" },
  });

  async function onSubmit(values: PasswordResetRequestValues) {
    setSubmitting(true);
    try {
      await requestPasswordReset(values.email);
      setSent(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao enviar link.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <PageShell
      kicker="Recuperar acesso"
      title="Vamos mandar um link pro seu email."
      description="Use o mesmo email com que você criou a conta. O link expira em 1 hora."
      size="narrow"
    >
      <Card className="mx-auto max-w-md p-2">
        <CardHeader>
          <CardTitle>Recuperação por email</CardTitle>
          <CardDescription>
            Já lembrou?{" "}
            <Link
              to="/entrar"
              className="text-[color:var(--color-foreground)] underline underline-offset-4 hover:text-[color:var(--color-accent)]"
            >
              Voltar para entrar
            </Link>
            .
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sent ? (
            <div className="rounded-sm border border-[--color-success]/40 bg-[color:var(--color-success-soft)] p-4 text-[length:var(--text-body-sm)]">
              <p className="flex items-center gap-2 font-medium">
                <CheckCircle2 className="size-4 text-[color:var(--color-success)]" aria-hidden />
                Email enviado.
              </p>
              <p className="mt-1 text-[color:var(--color-muted-foreground)]">
                Cheque sua caixa de entrada (e o spam). O link te leva pra definir uma nova senha.
              </p>
            </div>
          ) : (
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5" noValidate>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input
                  type="email"
                  autoComplete="email"
                  placeholder="voce@exemplo.com"
                  {...form.register("email")}
                />
                {form.formState.errors.email ? (
                  <p className="flex items-center gap-1.5 text-[length:var(--text-caption)] text-[color:var(--color-danger)]">
                    <AlertCircle className="size-3.5" aria-hidden />
                    {form.formState.errors.email.message}
                  </p>
                ) : null}
              </div>
              <Button type="submit" className="w-full" loading={submitting}>
                Enviar link
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </PageShell>
  );
}

export function PasswordResetPage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const form = useForm<PasswordResetValues>({
    resolver: zodResolver(passwordResetSchema),
    defaultValues: { password: "", confirm: "" },
  });

  async function onSubmit(values: PasswordResetValues) {
    setSubmitting(true);
    try {
      await updatePassword(values.password);
      toast.success("Senha atualizada. Bem-vinda de volta.");
      navigate("/painel", { replace: true });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao atualizar senha.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <PageShell
      kicker="Nova senha"
      title="Defina uma nova senha."
      description="Vai substituir a anterior em todos os dispositivos."
      size="narrow"
    >
      <Card className="mx-auto max-w-md p-2">
        <CardHeader>
          <CardTitle>Nova senha</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5" noValidate>
            <div className="space-y-1.5">
              <Label>Nova senha</Label>
              <Input
                type="password"
                autoComplete="new-password"
                placeholder="Mínimo de 6 caracteres"
                {...form.register("password")}
              />
              {form.formState.errors.password ? (
                <p className="flex items-center gap-1.5 text-[length:var(--text-caption)] text-[color:var(--color-danger)]">
                  <AlertCircle className="size-3.5" aria-hidden />
                  {form.formState.errors.password.message}
                </p>
              ) : null}
            </div>
            <div className="space-y-1.5">
              <Label>Confirmar</Label>
              <Input
                type="password"
                autoComplete="new-password"
                placeholder="Repita a senha"
                {...form.register("confirm")}
              />
              {form.formState.errors.confirm ? (
                <p className="flex items-center gap-1.5 text-[length:var(--text-caption)] text-[color:var(--color-danger)]">
                  <AlertCircle className="size-3.5" aria-hidden />
                  {form.formState.errors.confirm.message}
                </p>
              ) : null}
            </div>
            <Button type="submit" className="w-full" loading={submitting}>
              Atualizar senha
            </Button>
          </form>
        </CardContent>
      </Card>
    </PageShell>
  );
}
